import React, { useState } from 'react';
import PaymentModal from './PaymentModal';
import ConfirmModal from './shared/ConfirmModal';
import './AddPaymentPorterWizard.css';

/**
 * Shared payment step for the create wizards (Payment Porter, Sync, Opera Cloud Compare, …).
 *
 * Renders the canonical "Complete Setup → Payment Required → Make Payment" card, the superadmin
 * "⚙️ Mark as paid (admin)" bypass (with confirmation), the "Awaiting Payment" state after an
 * invoice is raised, and owns the PaymentModal wiring. Every wizard should render THIS instead of
 * hand-rolling the card, so Make Payment / Mark-as-paid / awaiting behaviour can never drift or go
 * missing on one wizard again.
 *
 * The parent owns the resource-specific logic and passes it in as callbacks/state:
 *   onSuccess(result)  – called with the PaymentModal result (create/update the record)
 *   onMarkPaid()       – superadmin bypass (create/update the record as paid); button shown only
 *                        when isSuperAdmin && onMarkPaid are both provided
 *   awaiting / invoiceResult / saving / error – parent-owned state
 */
const WizardPaymentStep = ({
    // Header / copy
    title = 'Complete Setup',
    subtitle,
    heading = 'Payment Required',
    description,
    // Parent-owned state
    awaiting = false,
    invoiceResult,
    awaitingMessage = 'Your invoice has been emailed. This will be activated once Stripe confirms the payment.',
    saving = false,
    error,
    // PaymentModal props
    hotelId,
    accountId,
    accountName = '',
    productId,
    invoicePrivilege,
    currentUser,
    isSuperAdmin = false,
    paymentService,
    onSuccess,
    // Superadmin bypass
    onMarkPaid,
    markPaidMessage = 'Mark this as paid manually? This bypasses Stripe and immediately activates it.',
    // Navigation
    onBack,
    backLabel = '← Back',
    onDone,            // optional "Done" button on the awaiting card
    doneLabel = 'Done',
}) => {
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showMarkPaid, setShowMarkPaid] = useState(false);

    if (awaiting) {
        return (
            <div className="ppw-card">
                <div className="ppw-card-header">
                    <h2 className="ppw-card-title">Awaiting Payment</h2>
                </div>
                <div className="ppw-card-body" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                    <p style={{ color: '#78350f', maxWidth: '520px', margin: '0 auto 0.75rem' }}>{awaitingMessage}</p>
                    {invoiceResult?.emailSentTo && (
                        <p style={{ color: '#78350f', fontSize: '0.9rem', marginTop: '0.75rem' }}>
                            Invoice sent to: <strong>{invoiceResult.emailSentTo}</strong>
                        </p>
                    )}
                    {invoiceResult?.hostedInvoiceUrl && (
                        <a href={invoiceResult.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer"
                           style={{ display: 'inline-block', marginTop: '0.75rem', color: '#4f46e5', fontWeight: 600, textDecoration: 'underline' }}>
                            View Payment Link
                        </a>
                    )}
                </div>
                {onDone && (
                    <div className="ppw-footer"><button className="ppw-btn-next" onClick={onDone}>{doneLabel}</button></div>
                )}
            </div>
        );
    }

    return (
        <div className="ppw-card">
            <div className="ppw-card-header">
                <h2 className="ppw-card-title">{title}</h2>
                {subtitle && <p className="ppw-card-subtitle">{subtitle}</p>}
            </div>
            <div className="ppw-card-body" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💳</div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e1b4b', marginBottom: '0.5rem' }}>{heading}</h2>
                {description && <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{description}</p>}
                {error && <div className="ppw-error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}
                <button type="button" className="ppw-btn-next" onClick={() => setShowPaymentModal(true)} disabled={saving}>
                    {saving ? 'Processing…' : 'Make Payment'}
                </button>
                {isSuperAdmin && onMarkPaid && (
                    <div style={{ marginTop: '1rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowMarkPaid(true)}
                            disabled={saving}
                            title="Superadmin: bypass Stripe and mark as paid"
                            style={{
                                background: '#fff', color: '#b45309', border: '1.5px solid #b45309',
                                padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem',
                                fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                            }}
                        >
                            ⚙️ Mark as paid (admin)
                        </button>
                    </div>
                )}
            </div>
            {onBack && (
                <div className="ppw-footer">
                    <button className="ppw-btn-back" onClick={onBack} disabled={saving}>{backLabel}</button>
                </div>
            )}

            {showPaymentModal && hotelId && (
                <PaymentModal
                    hotelId={hotelId}
                    accountId={accountId}
                    accountName={accountName}
                    productId={productId}
                    invoicePrivilege={invoicePrivilege}
                    currentUser={currentUser}
                    isSuperAdmin={isSuperAdmin}
                    paymentService={paymentService}
                    onSuccess={(result) => { setShowPaymentModal(false); onSuccess(result); }}
                    onCancel={() => setShowPaymentModal(false)}
                />
            )}

            {showMarkPaid && (
                <ConfirmModal
                    title="Mark as paid"
                    message={markPaidMessage}
                    confirmLabel="Mark as paid"
                    cancelLabel="Cancel"
                    onConfirm={() => { setShowMarkPaid(false); onMarkPaid(); }}
                    onCancel={() => setShowMarkPaid(false)}
                />
            )}
        </div>
    );
};

export default WizardPaymentStep;
